// Hook: manages the full backend execution lifecycle
// create → execute → poll → fetch artifacts

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createRun,
  executeRun,
  getRun,
  getRunEvents,
  getRunArtifacts,
  isTerminalStatus,
  type CreateRunPayload,
  type BackendRun,
  type BackendRunEvent,
  type BackendRunArtifact,
} from '@/lib/reqqyBackend';

const POLL_INTERVAL = 2000;

export interface RunExecutionState {
  runId: string | null;
  run: BackendRun | null;
  events: BackendRunEvent[];
  artifacts: BackendRunArtifact[];
  isCreating: boolean;
  isExecuting: boolean;
  isPolling: boolean;
  error: string | null;
}

export function useRunExecution() {
  const [state, setState] = useState<RunExecutionState>({
    runId: null,
    run: null,
    events: [],
    artifacts: [],
    isCreating: false,
    isExecuting: false,
    isPolling: false,
    error: null,
  });

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setState(s => ({ ...s, isPolling: false }));
  }, []);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const poll = useCallback(async (runId: string) => {
    try {
      const [run, events] = await Promise.all([
        getRun(runId),
        getRunEvents(runId),
      ]);

      let artifacts: BackendRunArtifact[] = [];
      if (isTerminalStatus(run.status)) {
        artifacts = await getRunArtifacts(runId);
      }

      setState(s => ({
        ...s,
        run,
        events,
        artifacts: artifacts.length > 0 ? artifacts : s.artifacts,
        error: null,
      }));

      if (isTerminalStatus(run.status)) {
        stopPolling();
      }
    } catch (err) {
      console.error('Polling error:', err);
    }
  }, [stopPolling]);

  const startPolling = useCallback((runId: string) => {
    stopPolling();
    runIdRef.current = runId;
    setState(s => ({ ...s, isPolling: true }));
    // Immediate first poll
    poll(runId);
    pollingRef.current = setInterval(() => poll(runId), POLL_INTERVAL);
  }, [poll, stopPolling]);

  const startRun = useCallback(async (payload: CreateRunPayload) => {
    setState(s => ({ ...s, isCreating: true, error: null, events: [], artifacts: [], run: null }));

    try {
      // Step 1: Create run
      const created = await createRun(payload);
      const runId = created.id;
      runIdRef.current = runId;

      setState(s => ({ ...s, runId, run: created, isCreating: false, isExecuting: true }));

      // Step 2: Execute run
      const executed = await executeRun(runId);
      setState(s => ({ ...s, run: executed, isExecuting: false }));

      // Step 3: Start polling
      startPolling(runId);

      return runId;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setState(s => ({ ...s, isCreating: false, isExecuting: false, error: message }));
      return null;
    }
  }, [startPolling]);

  const refreshRun = useCallback(async () => {
    const id = runIdRef.current;
    if (!id) return;
    try {
      const [run, events, artifacts] = await Promise.all([
        getRun(id),
        getRunEvents(id),
        getRunArtifacts(id),
      ]);
      setState(s => ({ ...s, run, events, artifacts, error: null }));
    } catch (err) {
      setState(s => ({ ...s, error: err instanceof Error ? err.message : String(err) }));
    }
  }, []);

  return {
    ...state,
    startRun,
    startPolling,
    stopPolling,
    refreshRun,
  };
}
